import debounce from 'lodash.debounce';
import UserManagementRepository from '@app/src/common/repository/UserManagementRepository';
import { SESSION_TABLE_KEY } from '@app/src/common/constants/common';
import AuthStore from '@app/src/common/store/AuthStore';
import {
  resolveActiveLocationId,
  setLocationRequestHeader
} from '@app/src/common/repository/locationRequestHeader';
import {
  ISessionData,
  ITableSessionValue
} from '@app/src/common/components/table/types';

export interface ISidebarPreferences {
  collapsed?: boolean;
}

export interface IUserPreferences {
  tables?: ITableSessionValue;
  sidebar?: ISidebarPreferences;
  locationId?: string;
}

const isTableSessionShape = (value: any): value is ITableSessionValue => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  if ('tables' in value || 'sidebar' in value || 'locationId' in value) {
    return false;
  }
  return Object.values(value).every(
    entry =>
      entry && typeof entry === 'object' && 'filters' in (entry as object)
  );
};

const readPreferences = (): IUserPreferences => {
  const raw = sessionStorage.getItem(SESSION_TABLE_KEY);
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    if (isTableSessionShape(parsed)) {
      return { tables: parsed };
    }
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
};

const writePreferences = (prefs: IUserPreferences) => {
  sessionStorage.setItem(SESSION_TABLE_KEY, JSON.stringify(prefs));
};

export default class UserPreferenceService {
  static updateUserPreferences = debounce((): Promise<unknown> => {
    if (AuthStore.isSupportLogin) {
      return Promise.resolve();
    }
    return UserManagementRepository.saveUserPreferences(
      AuthStore.userId,
      sessionStorage.getItem(SESSION_TABLE_KEY) || null
    );
  }, 500);

  // Sends a pending (debounced) preferences save right away and resolves once it settles.
  // Call before a full page reload, which would otherwise drop the pending save.
  static flushUserPreferences(): Promise<unknown> {
    return Promise.resolve(UserPreferenceService.updateUserPreferences.flush());
  }

  static resetPreferences() {
    const prefs = readPreferences();
    delete prefs.tables;
    delete prefs.locationId;
    if (Object.keys(prefs).length === 0) {
      sessionStorage.removeItem(SESSION_TABLE_KEY);
    } else {
      writePreferences(prefs);
    }
    // Revert the active location to the default (main) location so parking surfaces
    // stop using the cleared pick without needing a reload.
    setLocationRequestHeader(
      resolveActiveLocationId(AuthStore.user?.locations ?? [])
    );
    if (!AuthStore.isSupportLogin) {
      UserManagementRepository.saveUserPreferences(
        AuthStore.userId,
        sessionStorage.getItem(SESSION_TABLE_KEY) || null
      );
    }
  }

  static savePreferencesToStorage(preferences?: string) {
    if (preferences) {
      sessionStorage.setItem(SESSION_TABLE_KEY, preferences);
    } else if (UserPreferenceService.hasPreferences) {
      sessionStorage.removeItem(SESSION_TABLE_KEY);
    }
  }

  static getPreferencesForTable(tableSessionId: string) {
    try {
      const tables = readPreferences().tables || {};
      return tables[tableSessionId];
    } catch (e) {
      return null;
    }
  }

  static updatePreferencesForTable(tableSessionId: string, data: ISessionData) {
    try {
      const prefs = readPreferences();
      const tables = prefs.tables || {};
      tables[tableSessionId] = data;
      prefs.tables = tables;
      writePreferences(prefs);
      UserPreferenceService.updateUserPreferences();
    } catch (e) {
      return;
    }
  }

  static getLocationId(): string | undefined {
    return readPreferences().locationId;
  }

  static updateLocationId(locationId: string) {
    const prefs = readPreferences();
    prefs.locationId = locationId;
    writePreferences(prefs);
    UserPreferenceService.updateUserPreferences();
  }

  static getSidebarState(): ISidebarPreferences {
    return readPreferences().sidebar || {};
  }

  static updateSidebarState(sidebar: Partial<ISidebarPreferences>) {
    const prefs = readPreferences();
    prefs.sidebar = { ...(prefs.sidebar || {}), ...sidebar };
    writePreferences(prefs);
    UserPreferenceService.updateUserPreferences();
  }

  static get hasPreferences() {
    const prefs = readPreferences();
    return !!prefs.tables && Object.keys(prefs.tables).length > 0;
  }
}
